import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import FormularioCliente from '../Formularios/FormularioCliente';
import FormularioPoliza from '../Formularios/FormularioPoliza';

/**
 * RenovarPoliza - Orquestador del proceso de renovación.
 * Paso 1: Actualización del legajo del cliente (Smart Form).
 * Paso 2: Emisión de la nueva póliza usando los datos actualizados.
 */
const RenovarPoliza = ({ show, onClose, poliza, freshClientData, onSuccess }) => {
    const [step, setStep] = useState(1);
    const [activeId, setActiveId] = useState(null);
    
    // Estado donde guardamos el legajo confirmado tras el guardado del Paso 1
    const [updatedClientData, setUpdatedClientData] = useState(null);

    // Guardamos una referencia para saber si ya inicializamos esta póliza específica
    const initializedIdRef = useRef(null);

    // --- EFECTO DE REINICIO AL ABRIR ---
    useEffect(() => {
        if (show) {
            if (initializedIdRef.current !== poliza?.id) {
                setStep(1);
                setUpdatedClientData(null);
                setActiveId(poliza?.id);
                initializedIdRef.current = poliza?.id;
            }
        } else {
            // Limpiamos la referencia cuando el modal se cierra
            initializedIdRef.current = null;
        }
    }, [show, poliza?.id]);

    /**
     * clientInitialState:
     * Combina la info fresca del cliente con la estructura comercial de la póliza
     */
    const clientInitialState = useMemo(() => {
        if (!poliza || !show) return {};
        const c = freshClientData || {};
        
        return {
            ...c,
            canal: c.canal || poliza.canal || '',
            canalNombreId: c.canalNombreId || poliza.canalNombreId || '',
            canalNombre: c.canalNombre || poliza.canalNombre || '',
            subcanalNombreId: c.subcanalNombreId || poliza.subcanalNombreId || '',
            subcanalNombre: c.subcanalNombre || poliza.subcanalNombre || '',
            vendedor: c.vendedor || poliza.vendedor || ''
        };
    }, [freshClientData, poliza, show]);

    /**
     * handleClientSaveSuccess:
     * Avanzamos al Paso 2 con los datos guardados.
     */
    const handleClientSaveSuccess = (msg, savedData) => {
        setUpdatedClientData(savedData);
        setStep(2);
    };

    if (!show || !poliza) return null;

    return createPortal(
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 z-[1100] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity text-left font-sans">
            {/* Contenedor Principal: Altura fija de h-[90vh] para garantizar la estabilidad del layout */}
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden border border-gray-100 animate-fade-in relative text-left">
                
                {/* 1. Header Dinámico (Fijo) */}
                <div className="flex justify-between items-center px-8 py-5 border-b border-gray-100 bg-emerald-50/50 shrink-0">
                    <div className="text-left">
                        <h3 className="text-xl font-bold text-gray-800 leading-none">Renovar Póliza</h3>
                        <p className="text-xs text-emerald-600 font-bold uppercase mt-1.5 tracking-tighter italic">
                            Paso {step} de 2: {step === 1 ? 'Actualización y Validación de Cliente' : 'Detalles de la Nueva Póliza'}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-all active:scale-95 text-xl">✕</button>
                </div>

                {/* 2. Área de Contenido (Flexible y con min-h-0 para forzar scroll interno de hijos) */}
                <div className="flex-grow flex flex-col overflow-hidden min-h-0">
                    {step === 1 ? (
                        <div className="flex flex-col h-full overflow-hidden animate-fade-in min-h-0">
                            {/* Info Box: shrink-0 para que no se coma el espacio del scroll */}
                            <div className="px-8 pt-6 shrink-0">
                                <div className="p-4 bg-blue-50 border-l-4 border-blue-400 text-blue-800 text-sm rounded-r-lg">
                                    <p className="font-bold">Paso 1: Legajo del Asegurado</p>
                                    <p>Por favor, revisa y actualiza los datos de contacto y cobro antes de continuar.</p>
                                </div>
                            </div>
                            
                            {/* El formulario de cliente maneja su propio scroll */}
                            <div className="flex-grow overflow-hidden min-h-0">
                                <FormularioCliente 
                                    initialData={clientInitialState} 
                                    onSaveSuccess={handleClientSaveSuccess}
                                    onCancel={onClose} 
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="h-full overflow-hidden animate-fade-in flex flex-col min-h-0">
                            <FormularioPoliza 
                                mode="renewal"
                                cliente={updatedClientData}
                                initialData={poliza}
                                tipoCarga={poliza.rama}
                                onCancel={() => setStep(1)}
                                onSuccess={onSuccess}
                            />
                        </div>
                    )}
                </div>
            </div>
            
            <style>{`
                .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>,
        document.body
    );
};

export default RenovarPoliza;